import type { ElementType, ReactNode } from "react";

/**
 * The words around a figure, in the shared widget text roles (see tokens.css
 * "Widget text roles"). Use these, or the `text-metric-*` utilities they apply,
 * instead of sizing captions with text-xs / text-sm: the roles grow together on
 * a wall screen and in focus mode, so no widget has to adjust its own sizes.
 */

export type MetricLabelSize = "standard" | "compact";

const LABEL_CLASS: Record<MetricLabelSize, string> = {
  // What a primary (standard / headline) figure counts.
  standard: "text-metric-label text-secondary",
  // The label of a compact figure in a band.
  compact: "text-metric-caption font-medium text-secondary",
};

export function MetricLabel({
  children,
  size = "standard",
  as: Tag = "p",
  className = "",
  title,
}: {
  children: ReactNode;
  /** Match the size of the figure it labels: `compact` for compact MetricValue. */
  size?: MetricLabelSize;
  as?: ElementType;
  className?: string;
  title?: string;
}) {
  return (
    <Tag className={`min-w-0 break-words ${LABEL_CLASS[size]} ${className}`} title={title}>
      {children}
    </Tag>
  );
}

/** Context under or beside a figure: a unit of comparison, an ordinal, a reason. */
export function MetricDetail({
  children,
  as: Tag = "span",
  className = "",
  title,
}: {
  children: ReactNode;
  as?: ElementType;
  className?: string;
  title?: string;
}) {
  return (
    <Tag className={`text-metric-detail text-muted ${className}`} title={title}>
      {children}
    </Tag>
  );
}
